import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    endpoint: String,
    token: String,
    page: String
  }

  connect() {
    if (window.location.pathname.startsWith("/admin")) return
    if (!this.hasEndpointValue || !this.hasTokenValue) return

    this.pageStartedAt = Date.now()
    this.maxScrollDepth = 0
    this.sentExit = false
    this.observedElements = new WeakSet()
    this.attentionTimers = new WeakMap()

    this.trackScroll = this.trackScroll.bind(this)
    this.trackClick = this.trackClick.bind(this)
    this.trackExit = this.trackExit.bind(this)
    this.trackVisibilityChange = this.trackVisibilityChange.bind(this)

    window.addEventListener("scroll", this.trackScroll, { passive: true })
    document.addEventListener("click", this.trackClick)
    document.addEventListener("visibilitychange", this.trackVisibilityChange)
    window.addEventListener("pagehide", this.trackExit)
    document.addEventListener("turbo:before-cache", this.trackExit)

    this.observeAttentionTargets()
    this.send("page_view", this.pagePayload())
  }

  disconnect() {
    window.removeEventListener("scroll", this.trackScroll)
    document.removeEventListener("click", this.trackClick)
    document.removeEventListener("visibilitychange", this.trackVisibilityChange)
    window.removeEventListener("pagehide", this.trackExit)
    document.removeEventListener("turbo:before-cache", this.trackExit)
    this.observer?.disconnect()
  }

  observeAttentionTargets() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => this.handleIntersection(entry))
    }, { threshold: [0.5] })

    document.querySelectorAll("[data-usage-section], [data-usage-character]").forEach((element) => {
      this.observedElements.add(element)
      this.observer.observe(element)
    })
  }

  handleIntersection(entry) {
    const element = entry.target
    if (!entry.isIntersecting || entry.intersectionRatio < 0.5) {
      clearTimeout(this.attentionTimers.get(element))
      this.attentionTimers.delete(element)
      return
    }

    const payload = this.targetPayload(element)
    this.send(payload.target_type === "character" ? "character_view" : "section_view", payload)

    clearTimeout(this.attentionTimers.get(element))
    this.attentionTimers.set(element, setTimeout(() => {
      this.send("section_attention", {
        ...payload,
        metadata: {
          ...payload.metadata,
          visible_ratio: Math.round(entry.intersectionRatio * 100) / 100
        }
      })
    }, 2000))
  }

  trackClick(event) {
    const target = event.target.closest("[data-usage-action], [data-usage-character]")
    if (!target) return

    this.send("interaction", {
      ...this.targetPayload(target),
      action: target.dataset.usageAction || "click",
      metadata: {
        href: target.href || "",
        title: target.getAttribute("aria-label") || target.textContent.trim().slice(0, 120)
      }
    })
  }

  trackScroll() {
    const scrollable = document.documentElement.scrollHeight - window.innerHeight
    if (scrollable <= 0) {
      this.maxScrollDepth = 100
      return
    }

    const depth = Math.round((window.scrollY / scrollable) * 100)
    this.maxScrollDepth = Math.max(this.maxScrollDepth, Math.min(depth, 100))
  }

  trackVisibilityChange() {
    if (document.visibilityState === "hidden") this.trackExit()
  }

  trackExit() {
    if (this.sentExit) return
    this.sentExit = true
    this.trackScroll()
    this.send("page_exit", {
      ...this.pagePayload(),
      active_seconds: Math.max(1, Math.round((Date.now() - this.pageStartedAt) / 1000)),
      scroll_depth: this.maxScrollDepth
    }, true)
  }

  pagePayload() {
    return {
      path: window.location.pathname,
      page_key: this.pageValue || this.pageKeyFromPath(),
      metadata: {
        browser_language: navigator.language || "",
        title: document.title,
        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight
      }
    }
  }

  targetPayload(element) {
    const character = element.dataset.usageCharacter
    const section = element.dataset.usageSection

    return {
      ...this.pagePayload(),
      target_type: character ? "character" : "section",
      target_key: character || section || "",
      metadata: {
        section_kind: section || (character ? "character" : "")
      }
    }
  }

  send(name, payload = {}, keepalive = false) {
    const body = JSON.stringify({
      tracking_token: this.tokenValue,
      events: [{
        name,
        occurred_at: new Date().toISOString(),
        ...payload
      }]
    })

    fetch(this.endpointValue, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      keepalive,
      body
    }).catch(() => {})
  }

  pageKeyFromPath() {
    const path = window.location.pathname.replace(/^\/+/, "")
    return path.split("/")[0] || "home"
  }
}
